/**
 * Email validation utilities for business email verification
 * Blocks common free email providers
 */

// List of common free/personal email domains to block
const FREE_EMAIL_DOMAINS = new Set([
  // Gmail and Google
  'gmail.com',
  'googlemail.com',
  'google.com',

  // Outlook, Hotmail, Microsoft
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'microsoft.com',

  // Yahoo
  'yahoo.com',
  'ymail.com',

  // AOL
  'aol.com',
  'aim.com',

  // Proton Mail
  'protonmail.com',
  'pm.me',

  // Other popular free services
  'mail.com',
  'mailinator.com',
  'temp-mail.org',
  'tempmail.com',
  '10minutemail.com',
  'guerrillamail.com',
  'maildrop.cc',
  'zmail.ru',
  'yandex.com',

  // Regional free providers
  'qq.com',           // Tencent (China)
  'sina.com',         // Sina (China)
  '163.com',          // NetEase (China)
  'naver.com',        // Naver (Korea)
  'daum.net',         // Daum (Korea)
]);

/**
 * Validates if email is a business email (not a free email provider)
 * @param email - Email address to validate
 * @returns Object with isValid boolean and error message if invalid
 */
export const validateBusinessEmail = (email: string): { isValid: boolean; error?: string } => {
  // Basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  // Extract domain from email
  const domain = email.toLowerCase().split('@')[1];

  // Check if domain is in free email list
  if (FREE_EMAIL_DOMAINS.has(domain)) {
    return {
      isValid: false,
      error: 'Atlas is available for business users only. Please use your company email address instead of a personal email.'
    };
  }

  return { isValid: true };
};

/**
 * Checks if email domain is a free email provider
 * @param email - Email address to check
 * @returns true if it's a free email domain
 */
export const isFreeEmailDomain = (email: string): boolean => {
  const domain = email.toLowerCase().split('@')[1];
  return FREE_EMAIL_DOMAINS.has(domain);
};
